import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faGlobe,
  faDesktop,
  faGlobeAmericas,
  faCloud,
  faPlug,
  faBox,
  faUser,
  faNetworkWired,
  faEdit,
  faTrash,
  faPlus,
  faSpinner,
  faSearch,
  faFileImport,
  faTimes,
  faCheckCircle,
  faExclamationCircle,
  faChevronDown,
  faChevronUp,
  faCrosshairs
} from '@fortawesome/free-solid-svg-icons';
import targetService from '../../services/targetService';
import actionService from '../../services/actionService';
import TargetForm from './TargetForm';
import ActionDetailModal from '../common/ActionDetailModal';
import TargetDetailModal from './TargetDetailModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

const RESULT_COLORS = {
  successful: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  detected: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  failed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  partial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  unknown: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
};

const TargetList = ({ projectId: propProjectId, actions: propActions }) => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  // Get projectId from URL params if not passed as prop
  const { projectId: routeProjectId } = useParams();
  const projectId = propProjectId || routeProjectId;
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [expandedTarget, setExpandedTarget] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [localActions, setLocalActions] = useState([]);

  // Use propActions if provided, otherwise fetch our own
  const actions = propActions || localActions;

  // Get actions involving a specific target (as target OR source)
  const getActionsForTarget = (targetId) => {
    if (!actions || actions.length === 0) return [];
    const getId = (ref) => {
      if (typeof ref === 'string') return ref;
      const id = ref?._id || ref?.id;
      return id ? String(id) : null;
    };
    const tid = String(targetId);
    return actions.filter(action => {
      const inTargets = Array.isArray(action.targets) && action.targets.some(t => getId(t) === tid);
      const inSources = Array.isArray(action.sources) && action.sources.some(s => getId(s) === tid);
      return inTargets || inSources;
    });
  };

  useEffect(() => {
    fetchTargets();
    // Fetch actions if not provided as prop
    if (!propActions) {
      actionService.getActionsByProject(projectId)
        .then(data => setLocalActions(Array.isArray(data) ? data : []))
        .catch(err => console.error('Error fetching actions:', err));
    }
  }, [projectId, propActions]);

  const fetchTargets = async () => {
    try {
      const data = await targetService.getTargetsByProject(projectId);
      setTargets(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching targets:', error);
      toast.error('Failed to load targets');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this target?')) {
      try {
        await targetService.deleteTarget(id);
        toast.success('Target deleted successfully');
        fetchTargets();
      } catch (error) {
        toast.error('Failed to delete target');
      }
    }
  };

  const handleEdit = (target) => {
    setEditingTarget(target);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingTarget(null);
    fetchTargets();
  };

  // Parse bulk import text into target objects
  const parseTargets = (text) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    const parsed = [];

    for (const line of lines) {
      // Skip comments
      if (line.startsWith('#') || line.startsWith('//')) continue;

      // Handle different formats:
      // - Simple: 192.168.1.1 or hostname.domain.com
      // - With description: 192.168.1.1 - Web Server
      // - CIDR notation: 192.168.1.0/24
      // - IP ranges: 192.168.1.1-192.168.1.10

      let value = line;
      let description = '';

      // Check for description separator (-, :, or tab)
      const separators = [' - ', ' : ', '\t', '  '];
      for (const sep of separators) {
        if (line.includes(sep)) {
          const parts = line.split(sep);
          value = parts[0].trim();
          description = parts.slice(1).join(sep).trim();
          break;
        }
      }

      // Check if it's an IP range (e.g., 192.168.1.1-192.168.1.10)
      if (value.match(/^\d+\.\d+\.\d+\.\d+-\d+\.\d+\.\d+\.\d+$/)) {
        const [start, end] = value.split('-');
        const startParts = start.split('.').map(Number);
        const endParts = end.split('.').map(Number);

        // Only expand if in same /24 subnet and reasonable range
        if (
          startParts[0] === endParts[0] &&
          startParts[1] === endParts[1] &&
          startParts[2] === endParts[2] &&
          endParts[3] - startParts[3] <= 255
        ) {
          for (let i = startParts[3]; i <= endParts[3]; i++) {
            parsed.push({
              value: `${startParts[0]}.${startParts[1]}.${startParts[2]}.${i}`,
              description: description || `Range: ${value}`
            });
          }
          continue;
        }
      }

      // Check if it's CIDR notation (e.g., 192.168.1.0/24)
      if (value.match(/^\d+\.\d+\.\d+\.\d+\/\d+$/)) {
        const [ip, cidr] = value.split('/');
        const cidrNum = parseInt(cidr);
        const ipParts = ip.split('.').map(Number);

        // Only expand /24 and smaller networks (up to 256 hosts)
        if (cidrNum >= 24 && cidrNum <= 32) {
          const hostBits = 32 - cidrNum;
          const numHosts = Math.pow(2, hostBits);

          for (let i = 0; i < numHosts; i++) {
            const lastOctet = ipParts[3] + i;
            if (lastOctet <= 255) {
              parsed.push({
                value: `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.${lastOctet}`,
                description: description || `CIDR: ${value}`
              });
            }
          }
          continue;
        } else {
          // For larger networks, just add the CIDR notation as-is
          parsed.push({ value, description });
          continue;
        }
      }

      // Regular IP or hostname
      if (value) {
        parsed.push({ value, description });
      }
    }

    return parsed;
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      toast.error('Please enter some targets to import');
      return;
    }

    setBulkImportLoading(true);
    setBulkImportResult(null);

    try {
      const targets = parseTargets(bulkImportText);

      if (targets.length === 0) {
        toast.error('No valid targets found in the input');
        setBulkImportLoading(false);
        return;
      }

      const result = await targetService.bulkCreateTargets(projectId, targets);

      setBulkImportResult({
        success: true,
        inserted: result.added || 0,
        duplicates: result.duplicateValues || []
      });

      if (result.added > 0) {
        toast.success(`Successfully imported ${result.added} targets`);
        fetchTargets();
      }

      if (result.duplicates > 0) {
        toast.info(`${result.duplicates} duplicates were skipped`);
      }
    } catch (error) {
      console.error('Bulk import error:', error);
      toast.error(error.response?.data?.message || 'Failed to import targets');
      setBulkImportResult({
        success: false,
        error: error.response?.data?.message || 'Import failed'
      });
    } finally {
      setBulkImportLoading(false);
    }
  };

  const closeBulkImport = () => {
    setShowBulkImport(false);
    setBulkImportText('');
    setBulkImportResult(null);
  };

  const getTargetIcon = (type) => {
    const icons = {
      ip: faGlobe,
      hostname: faDesktop,
      cloudResource: faCloud,
      apiEndpoint: faPlug,
      container: faBox,
      identity: faUser,
      webapplication: faGlobeAmericas,
      network: faNetworkWired,
      application: faGlobeAmericas
    };
    return icons[type] || faGlobe;
  };

  const getTargetTypeColor = (type) => {
    const colors = {
      ip: 'bg-blue-100 text-blue-800 border-blue-200',
      hostname: 'bg-green-100 text-green-800 border-green-200',
      webapplication: 'bg-teal-100 text-teal-800 border-teal-200',
      api: 'bg-teal-100 text-teal-800 border-teal-200',
      network: 'bg-purple-100 text-purple-800 border-purple-200',
      cloudResource: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      apiEndpoint: 'bg-orange-100 text-orange-800 border-orange-200',
      container: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      identity: 'bg-pink-100 text-pink-800 border-pink-200',
      application: 'bg-teal-100 text-teal-800 border-teal-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const typeLabels = {
    ip: t('targets.ipAddresses', 'IP Addresses'),
    hostname: t('targets.hostnames', 'Hostnames'),
    webapplication: t('targets.webApplications', 'Web Applications'),
    api: t('targets.apiTargets', 'APIs'),
    network: t('targets.networkSegments', 'Network Segments'),
    cloudResource: t('targets.cloudResources', 'Cloud Resources'),
    apiEndpoint: t('targets.apiEndpoints', 'API Endpoints'),
    container: t('targets.containers', 'Containers'),
    identity: t('targets.identities', 'Identities'),
    application: t('targets.applications', 'Applications')
  };

  // Filter targets based on search and type
  const filteredTargets = targets.filter(target => {
    const matchesSearch = target.value.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (target.description && target.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === 'all' || target.type === filterType;
    return matchesSearch && matchesType;
  });

  // Group targets by type for better organization
  const groupedTargets = filteredTargets.reduce((acc, target) => {
    if (!acc[target.type]) {
      acc[target.type] = [];
    }
    acc[target.type].push(target);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <div>
      {/* Header and Controls */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('targets.targetManagement')}</h3>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('targets.targetManagementDesc')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowBulkImport(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
          >
            <FontAwesomeIcon icon={faFileImport} className="mr-2" />
            {t('targets.bulkImport')}
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            {t('targets.addTarget')}
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder={t('targets.searchTargets')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full px-4 py-2 pl-10 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
          />
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className={`px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
        >
          <option value="all">{t('targets.allTypes')}</option>
          <option value="ip">{t('targets.ipAddresses')}</option>
          <option value="hostname">{t('targets.hostnames')}</option>
          <option value="webapplication">{t('targets.webApplications', 'Web Applications')}</option>
          <option value="network">{t('targets.networkSegments', 'Network Segments')}</option>
          <option value="cloudResource">{t('targets.cloudResources', 'Cloud Resources')}</option>
          <option value="apiEndpoint">{t('targets.apiEndpoints', 'API Endpoints')}</option>
          <option value="container">{t('targets.containers', 'Containers')}</option>
          <option value="identity">{t('targets.identities', 'Identities')}</option>
        </select>
      </div>

      {/* Target Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className={`rounded-lg shadow-sm border p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{targets.length}</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('targets.totalTargets')}</p>
            </div>
            <FontAwesomeIcon icon={faBullseye} className="text-3xl text-primary" />
          </div>
        </div>
        <div className={`rounded-lg shadow-sm border p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {targets.filter(t => t.type === 'ip').length}
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('targets.ipAddresses')}</p>
            </div>
            <FontAwesomeIcon icon={faGlobe} className="text-3xl text-blue-600" />
          </div>
        </div>
        <div className={`rounded-lg shadow-sm border p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-green-600">
                {targets.filter(t => t.type === 'hostname').length}
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('targets.hostnames')}</p>
            </div>
            <FontAwesomeIcon icon={faDesktop} className="text-3xl text-green-600" />
          </div>
        </div>
      </div>

      {/* Targets List */}
      {filteredTargets.length === 0 ? (
        <div className={`text-center py-10 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <FontAwesomeIcon icon={faBullseye} className={`text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
          <p className={`text-lg mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {searchQuery || filterType !== 'all'
              ? t('targets.noMatchingTargets')
              : t('targets.noTargetsDefined')}
          </p>
          {!searchQuery && filterType === 'all' && (
            <>
              <p className={`mb-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('targets.addTargetsDescription')}
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center mx-auto"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t('targets.addFirstTargetBtn')}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedTargets).map(([type, typeTargets]) => (
            <div key={type}>
              <h4 className={`font-medium mb-3 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <FontAwesomeIcon
                  icon={getTargetIcon(type)}
                  className="mr-2 text-primary"
                />
                {typeLabels[type] || type}
                <span className={`ml-2 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>({typeTargets.length})</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {typeTargets.map((target) => (
                  <div
                    key={target._id}
                    onClick={() => setDetailTarget(target)}
                    className={`rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow cursor-pointer ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={getTargetIcon(target.type)}
                          className="text-2xl mr-3 text-primary"
                        />
                        <div>
                          <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{target.value}</h5>
                          {target.hostname && target.hostname !== target.value && (
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{target.hostname}{target.ip ? ` (${target.ip})` : ''}</span>
                          )}
                          <span className={`inline-block px-2 py-1 text-xs rounded-full border ${getTargetTypeColor(target.type)} mt-1`}>
                            {target.type}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(target); }}
                          className={`p-1 text-blue-600 hover:text-blue-800 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-blue-50'}`}
                          title={t('common.edit')}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(target._id); }}
                          className={`p-1 text-red-600 hover:text-red-800 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-red-50'}`}
                          title={t('common.delete')}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                    {target.description && (
                      <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{target.description}</p>
                    )}
                    {target.notes && (
                      <div className={`mt-3 p-2 rounded text-xs ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                        <span className="font-medium">{t('targets.notes')}:</span> {target.notes}
                      </div>
                    )}
                    {/* Action badges - always visible */}
                    {(() => {
                      const targetActions = getActionsForTarget(target._id);
                      const successful = targetActions.filter(a => a.result === 'successful').length;
                      const detected = targetActions.filter(a => a.result === 'detected').length;
                      const blocked = targetActions.filter(a => a.result === 'blocked').length;
                      const failed = targetActions.filter(a => a.result === 'failed').length;
                      const partial = targetActions.filter(a => a.result === 'partial').length;
                      const isExpanded = expandedTarget === target._id;

                      return (
                        <div className="mt-3">
                          <div
                            onClick={(e) => { e.stopPropagation(); if (targetActions.length > 0) setExpandedTarget(isExpanded ? null : target._id); }}
                            className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
                              targetActions.length > 0
                                ? (darkMode ? 'bg-gray-700 hover:bg-gray-600 cursor-pointer' : 'bg-gray-100 hover:bg-gray-200 cursor-pointer')
                                : (darkMode ? 'bg-gray-700/50' : 'bg-gray-50')
                            }`}
                          >
                            <span className="flex items-center gap-1.5">
                              <FontAwesomeIcon icon={faCrosshairs} className={targetActions.length > 0 ? 'text-red-500' : (darkMode ? 'text-gray-600' : 'text-gray-400')} />
                              <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {targetActions.length} {t('common.actions')}
                              </span>
                              {targetActions.length > 0 && (
                                <span className="flex items-center gap-1 ml-1">
                                  {successful > 0 && <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">{successful}✓</span>}
                                  {detected > 0 && <span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">{detected}!</span>}
                                  {blocked > 0 && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">{blocked}✗</span>}
                                  {failed > 0 && <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300">{failed}−</span>}
                                  {partial > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">{partial}~</span>}
                                </span>
                              )}
                            </span>
                            {targetActions.length > 0 && (
                              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                            )}
                          </div>
                          {isExpanded && targetActions.length > 0 && (
                            <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                              {targetActions.map(action => (
                                <div
                                  key={action._id}
                                  onClick={(e) => { e.stopPropagation(); setSelectedAction(action); }}
                                  className={`flex items-center justify-between p-2 rounded cursor-pointer text-xs ${
                                    darkMode ? 'bg-gray-900 hover:bg-gray-700 text-gray-300' : 'bg-gray-50 hover:bg-blue-50 text-gray-700'
                                  }`}
                                  title={action.title}
                                >
                                  <div className="flex-1 min-w-0 mr-2">
                                    <div className={`font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                      {action.title}
                                    </div>
                                    {action.mitreAttackTechnique && (
                                      <div className="text-purple-500 truncate">
                                        {action.mitreAttackTechnique.id} - {action.mitreAttackTechnique.name}
                                      </div>
                                    )}
                                  </div>
                                  <span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${RESULT_COLORS[action.result] || RESULT_COLORS.unknown}`}>
                                    {action.result || 'unknown'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div className={`mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {t('targets.added')}: {new Date(target.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Target Form Modal - Fixed props */}
      {showForm && (
        <TargetForm
          targetData={editingTarget}
          isEditing={!!editingTarget}
          onClose={handleFormClose}
        />
      )}

      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => setSelectedAction(updated)}
      />

      {/* Target Detail Modal */}
      <TargetDetailModal
        target={detailTarget}
        actions={actions}
        allTargets={targets}
        isOpen={!!detailTarget}
        onClose={() => setDetailTarget(null)}
        projectId={projectId}
      />

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faFileImport} className="mr-2 text-green-600" />
                {t('targets.bulkImport')}
              </h2>
              <button
                onClick={closeBulkImport}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Enter one target per line. Supported formats:
              </p>
              <ul className="text-xs text-gray-500 list-disc list-inside mb-3 space-y-1">
                <li>IP addresses: <code className="bg-gray-100 px-1 rounded">192.168.1.1</code></li>
                <li>Hostnames: <code className="bg-gray-100 px-1 rounded">server.example.com</code></li>
                <li>CIDR notation: <code className="bg-gray-100 px-1 rounded">192.168.1.0/24</code> (auto-expands for /24-/32)</li>
                <li>IP ranges: <code className="bg-gray-100 px-1 rounded">192.168.1.1-192.168.1.10</code></li>
                <li>With description: <code className="bg-gray-100 px-1 rounded">192.168.1.1 - Web Server</code></li>
                <li>Comments: Lines starting with <code className="bg-gray-100 px-1 rounded">#</code> or <code className="bg-gray-100 px-1 rounded">//</code> are ignored</li>
              </ul>
            </div>

            <textarea
              value={bulkImportText}
              onChange={(e) => setBulkImportText(e.target.value)}
              placeholder={`# Example targets
192.168.1.1 - Domain Controller
192.168.1.10 - Web Server
192.168.1.20-192.168.1.25 - Workstations
mail.example.com - Mail Server
192.168.2.0/28 - DMZ Segment`}
              className={`w-full h-64 px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500 font-mono text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
              disabled={bulkImportLoading}
            />

            {/* Preview */}
            {bulkImportText.trim() && !bulkImportResult && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Preview: {parseTargets(bulkImportText).length} targets will be imported
                </p>
                <div className="max-h-32 overflow-y-auto">
                  <div className="flex flex-wrap gap-1">
                    {parseTargets(bulkImportText).slice(0, 20).map((t, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                        {t.value}
                      </span>
                    ))}
                    {parseTargets(bulkImportText).length > 20 && (
                      <span className="px-2 py-1 bg-gray-200 rounded text-xs text-gray-600">
                        +{parseTargets(bulkImportText).length - 20} more...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Result */}
            {bulkImportResult && (
              <div className={`mt-3 p-3 rounded-lg ${bulkImportResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                {bulkImportResult.success ? (
                  <>
                    <p className="flex items-center text-green-700 font-medium mb-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                      Import completed
                    </p>
                    {bulkImportResult.inserted > 0 && (
                      <p className="text-sm text-green-600">
                        {bulkImportResult.inserted} targets imported successfully
                      </p>
                    )}
                    {bulkImportResult.duplicates?.length > 0 && (
                      <p className="text-sm text-yellow-600 mt-1">
                        {bulkImportResult.duplicates.length} duplicates skipped: {bulkImportResult.duplicates.slice(0, 5).join(', ')}
                        {bulkImportResult.duplicates.length > 5 && '...'}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="flex items-center text-red-700 font-medium">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
                    {bulkImportResult.error}
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={closeBulkImport}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                disabled={bulkImportLoading}
              >
                {bulkImportResult ? 'Close' : 'Cancel'}
              </button>
              {!bulkImportResult && (
                <button
                  onClick={handleBulkImport}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 flex items-center"
                  disabled={bulkImportLoading || !bulkImportText.trim()}
                >
                  {bulkImportLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />}
                  {bulkImportLoading ? 'Importing...' : 'Import Targets'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TargetList;